import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '../components/Navbar';

const API_BASE_URL = process.env.NODE_ENV === 'production' 
  ? '' // Empty string for production (relative URL)
  : 'http://localhost:5001';

const SignupPage = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    username: '',
    password: '',
    passwordConfirm: ''
  });
  
  // Validation and submission state
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [serverError, setServerError] = useState('');
  const [usernameAvailable, setUsernameAvailable] = useState(null);
  const [emailAvailable, setEmailAvailable] = useState(null);
  const [usernameChecking, setUsernameChecking] = useState(false);
  const [emailChecking, setEmailChecking] = useState(false);
  
  // Debounce timers for checks
  const [usernameTimer, setUsernameTimer] = useState(null);
  const [emailTimer, setEmailTimer] = useState(null);
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    
    // Clear specific error when field is edited
    if (errors[name]) {
      setErrors({ ...errors, [name]: '' });
    }
    
    // Special handling for username field
    if (name === 'username') {
      // Reset the availability status
      setUsernameAvailable(null);
      
      // Clear previous timer
      if (usernameTimer) clearTimeout(usernameTimer);
      
      // Check if username has required minimum length
      if (value.length >= 4) {
        // Check if username has valid format
        if (/^[a-zA-Z0-9_]+$/.test(value)) {
          setUsernameChecking(true);
          
          // Set new timer to check username availability
          const timer = setTimeout(() => {
            checkUsernameAvailability(value);
          }, 800); // Wait for 800ms of inactivity
          
          setUsernameTimer(timer);
        } else {
          // Show immediate validation error for invalid characters
          setErrors({
            ...errors,
            username: t('signup.errors.invalidUsername', 'Username must contain only letters, numbers, and underscores')
          });
        }
      }
    }
    
    // Special handling for email field
    if (name === 'email') {
      // Reset the availability status
      setEmailAvailable(null);
      
      // Clear previous timer
      if (emailTimer) clearTimeout(emailTimer);
      
      // Only check if email has a valid format
      if (/\S+@\S+\.\S+/.test(value)) {
        setEmailChecking(true);
        
        // Set new timer to check email availability
        const timer = setTimeout(() => {
          checkEmailAvailability(value);
        }, 800); // Wait for 800ms of inactivity
        
        setEmailTimer(timer);
      }
    }
  };
  
  const checkUsernameAvailability = async (username) => {
    try {
      setUsernameChecking(true);
      
      // First do client-side validation
      if (!/^[a-zA-Z0-9_]+$/.test(username)) {
        setUsernameAvailable(false);
        setErrors({
          ...errors,
          username: data.message || t('signup.errors.usernameTaken', 'This username is already taken')
        });
        return;
      }
      
      // Check if username is available using backend API
      const response = await fetch(`${API_BASE_URL}/api/check-username/${username}`);
      const data = await response.json();
      
      setUsernameAvailable(data.available);
      
      if (!data.available) {
        setErrors({
          ...errors,
          username: data.message || t('signup.errors.usernameTaken', 'This username is already taken')
        });
      } else {
        // Clear the error if username is available
        setErrors({
          ...errors,
          username: ''
        });
      }
    } catch (error) {
      console.error('Error checking username:', error);
      setUsernameAvailable(false);
      setErrors({
        ...errors,
        username: t('signup.errors.checkFailed', 'Failed to check username availability')
      });
    } finally {
      setUsernameChecking(false);
    }
  };
  
  const checkEmailAvailability = async (email) => {
    try {
      setEmailChecking(true);
      
      // First do client-side validation
      if (!/\S+@\S+\.\S+/.test(email)) {
        setEmailAvailable(false);
        setErrors({
          ...errors,
          email: t('signup.errors.invalidEmail', 'Please enter a valid email address')
        });
        return;
      }
      
      // Check if email is available using backend API
      const response = await fetch(`${API_BASE_URL}/api/check-email/${email}`);
      const data = await response.json();
      
      setEmailAvailable(data.available);
      
      if (!data.available) {
        setErrors({
          ...errors,
          email: data.message || t('signup.errors.emailTaken', 'This email is already registered')
        });
      } else {
        // Clear the error if email is available
        setErrors({
          ...errors,
          email: ''
        });
      }
    } catch (error) {
      console.error('Error checking email:', error);
      setEmailAvailable(false);
      setErrors({
        ...errors,
        email: t('signup.errors.checkFailed', 'Failed to check email availability')
      });
    } finally {
      setEmailChecking(false);
    }
  };
  
  const validateForm = () => {
    const newErrors = {};
    
    // Name validation
    if (!formData.name.trim()) {
      newErrors.name = t('signup.errors.required', 'This field is required');
    } else if (formData.name.trim().length < 2) {
      newErrors.name = t('signup.errors.nameLength', 'Name must be at least 2 characters');
    }
    
    // Email validation
    if (!formData.email.trim()) {
      newErrors.email = t('signup.errors.required', 'This field is required');
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = t('signup.errors.invalidEmail', 'Please enter a valid email address');
    } else if (emailAvailable === false) {
      newErrors.email = t('signup.errors.emailTaken', 'This email is already registered');
    }
    
    // Username validation
    if (!formData.username.trim()) {
      newErrors.username = t('signup.errors.required', 'This field is required');
    } else if (formData.username.trim().length < 4) {
      newErrors.username = t('signup.errors.usernameLength', 'Username must be at least 4 characters');
    } else if (!/^[a-zA-Z0-9_]+$/.test(formData.username)) {
      newErrors.username = t('signup.errors.invalidUsername', 'Username must contain only letters, numbers, and underscores');
    } else if (usernameAvailable === false) {
      newErrors.username = t('signup.errors.usernameTaken', 'This username is already taken');
    }
    
    // Password validation
    if (!formData.password) {
      newErrors.password = t('signup.errors.required', 'This field is required');
    } else if (formData.password.length < 12) {
      newErrors.password = t('signup.errors.passwordLength', 'Password must be at least 12 characters');
    }
    
    // Confirm password validation
    if (formData.password !== formData.passwordConfirm) {
      newErrors.passwordConfirm = t('signup.errors.passwordMismatch', 'Passwords do not match');
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Clear previous errors
    setServerError('');
    
    // Validate form
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    
    // Prepare request payload
    const requestData = {
      name: formData.name,
      email: formData.email,
      username: formData.username,
      password: encodeURIComponent(formData.password),
      language_preference: i18n.language // Include current language preference
    };
    
    // Debug logging
    console.log('Signup form data being sent:', {
      name: requestData.name,
      email: requestData.email,
      username: requestData.username,
      password: '***REDACTED***',
      language_preference: requestData.language_preference
    });
    
    try {
      console.log('Sending registration request to:', `${API_BASE_URL}/api/register`);
      
      const response = await fetch(`${API_BASE_URL}/api/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestData)
      });
      
      // Log response status
      console.log('Registration response status:', response.status);
      
      const data = await response.json();
      console.log('Registration response data:', data);
      
      if (!response.ok) {
        throw new Error(data.error || 'An error occurred during registration');
      }
      
      // Redirect to verification sent page or show success message
      navigate('/login', { 
        state: { 
          registrationSuccess: true,
          verificationRequired: true,
          email: formData.email 
        } 
      });
    } catch (error) {
      console.error('Registration error:', error);
      setServerError(error.message || t('signup.errors.registration', 'Registration failed. Please try again.'));
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // Clean up timers on unmount
  useEffect(() => {
    return () => {
      if (usernameTimer) clearTimeout(usernameTimer);
      if (emailTimer) clearTimeout(emailTimer);
    };
  }, [usernameTimer, emailTimer]);
  
  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      <div className="max-w-md mx-auto p-6">
        <div className="bg-white shadow-md rounded-lg p-8">
          <h1 className="text-3xl font-bold mb-6 text-center">
            {t('signup.title', 'Create Your Account')}
          </h1>
          
          {serverError && (
            <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded-md">
              {serverError}
            </div>
          )}
          
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-gray-700 mb-2" htmlFor="name">
                {t('signup.name', 'Your Name')} *
              </label>
              <input
                id="name"
                name="name"
                type="text"
                value={formData.name}
                onChange={handleChange}
                className={`w-full p-3 border rounded-md ${errors.name ? 'border-red-500' : 'border-gray-300'}`}
                placeholder={t('signup.namePlaceholder', 'e.g., John Smith')}
              />
              {errors.name && (
                <p className="text-red-500 text-sm mt-1">{errors.name}</p>
              )}
            </div>
            
            <div>
              <label className="block text-gray-700 mb-2" htmlFor="email">
                {t('signup.email', 'Email Address')} *
              </label>
              <div className="relative">
                <input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  className={`w-full p-3 border rounded-md ${errors.email ? 'border-red-500' : emailAvailable === true ? 'border-green-500' : 'border-gray-300'}`}
                  placeholder={t('signup.emailPlaceholder', 'e.g., john@example.com')}
                />
                {emailChecking && (
                  <div className="absolute right-3 top-3">
                    <svg className="animate-spin h-5 w-5 text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  </div>
                )}
                {!emailChecking && emailAvailable === true && (
                  <div className="absolute right-3 top-3 text-green-500">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                  </div>
                )}
              </div>
              {errors.email && (
                <p className="text-red-500 text-sm mt-1">{errors.email}</p>
              )}
            </div>
            
            <div>
              <label className="block text-gray-700 mb-2" htmlFor="username">
                {t('signup.username', 'Username')} *
              </label>
              <div className="relative">
                <input
                  id="username"
                  name="username"
                  type="text"
                  value={formData.username}
                  onChange={handleChange}
                  className={`w-full p-3 border rounded-md ${errors.username ? 'border-red-500' : usernameAvailable === true ? 'border-green-500' : 'border-gray-300'}`}
                  placeholder={t('signup.usernamePlaceholder', 'e.g., johnsmith')}
                />
                {usernameChecking && (
                  <div className="absolute right-3 top-3">
                    <svg className="animate-spin h-5 w-5 text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  </div>
                )}
                {!usernameChecking && usernameAvailable === true && (
                  <div className="absolute right-3 top-3 text-green-500">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                  </div>
                )}
              </div>
              {errors.username && (
                <p className="text-red-500 text-sm mt-1">{errors.username}</p>
              )}
              <p className="text-sm text-gray-500 mt-1">
                {t('signup.usernameHint', 'Only letters, numbers, and underscores (_) are allowed. You will use this to log in.')}
              </p>
            </div>
            
            <div>
              <label className="block text-gray-700 mb-2" htmlFor="password">
                {t('signup.password', 'Password')} *
              </label>
              <input
                id="password"
                name="password"
                type="password"
                value={formData.password}
                onChange={handleChange}
                className={`w-full p-3 border rounded-md ${errors.password ? 'border-red-500' : 'border-gray-300'}`}
              />
              {errors.password && (
                <p className="text-red-500 text-sm mt-1">{errors.password}</p>
              )}
              <p className="text-sm text-gray-500 mt-1">
                {t('signup.passwordHint', 'Min. 12 characters')}
              </p>
            </div>
            
            <div>
              <label className="block text-gray-700 mb-2" htmlFor="passwordConfirm">
                {t('signup.confirmPassword', 'Confirm Password')} *
              </label>
              <input
                id="passwordConfirm"
                name="passwordConfirm"
                type="password"
                value={formData.passwordConfirm}
                onChange={handleChange}
                className={`w-full p-3 border rounded-md ${errors.passwordConfirm ? 'border-red-500' : 'border-gray-300'}`}
              />
              {errors.passwordConfirm && (
                <p className="text-red-500 text-sm mt-1">{errors.passwordConfirm}</p>
              )}
            </div>
            
            <div className="pt-4">
              <button
                type="submit"
                disabled={isSubmitting || usernameChecking || emailChecking}
                className="w-full bg-blue-600 text-white py-3 rounded-md font-medium hover:bg-blue-700 transition-colors disabled:bg-blue-400 disabled:cursor-not-allowed"
              >
                {isSubmitting ? t('signup.submitting', 'Creating account...') : t('signup.submit', 'Create Account')}
              </button>
              
              <p className="text-center mt-4 text-gray-600">
                {t('signup.alreadyHaveAccount', 'Already have an account?')}{' '}
                <Link to="/login" className="text-blue-600 hover:underline">
                  {t('signup.login', 'Log in')}
                </Link>
              </p>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default SignupPage; 